import type { ActionView } from './action-confirmation';

export type PendingActionEntry = {
  scope: string;
  action: ActionView;
  element: HTMLElement | null;
  visible: boolean;
  presented: boolean;
};
const empty: readonly PendingActionEntry[] = [];

/** Mounted presentations only, scoped to the signed-in person/company/thread. */
export function createPendingActionStore() {
  const entries = new Map<symbol, PendingActionEntry>();
  const listeners = new Set<() => void>();
  const snapshots = new Map<string, readonly PendingActionEntry[]>();
  const changed = () => {
    snapshots.clear();
    listeners.forEach((listener) => listener());
  };
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    put(key: symbol, entry: PendingActionEntry) {
      entries.set(key, entry);
      changed();
    },
    remove(key: symbol) {
      if (entries.delete(key)) changed();
    },
    snapshot(scope: string): readonly PendingActionEntry[] {
      if (!scope) return empty;
      const cached = snapshots.get(scope);
      if (cached) return cached;
      const all = [...entries.values()].filter((entry) => entry.scope === scope);
      const terminal = new Set(
        all.filter(({ action }) => action.status !== 'pending').map(({ action }) => action.id),
      );
      const byId = new Map<string, PendingActionEntry>();
      for (const entry of all) {
        if (entry.action.status !== 'pending' || terminal.has(entry.action.id)) continue;
        const previous = byId.get(entry.action.id);
        byId.set(
          entry.action.id,
          previous
            ? {
                ...entry,
                presented: previous.presented || entry.presented,
                visible: previous.visible || entry.visible,
                element: previous.element?.isConnected ? previous.element : entry.element,
              }
            : entry,
        );
      }
      const result = [...byId.values()];
      snapshots.set(scope, result);
      return result;
    },
  };
}

export const pendingActionStore = createPendingActionStore();
export const pendingActionScope = (
  userId: string | undefined,
  companyId: string | undefined,
  threadId: string | undefined,
) => (userId && companyId && threadId ? JSON.stringify([userId, companyId, threadId]) : '');

export function messageApprovalCandidate(entries: readonly PendingActionEntry[]) {
  if (entries.length !== 1) return null;
  const entry = entries[0];
  return entry.presented &&
    entry.action.canDecide &&
    !entry.action.editing &&
    entry.action.messageApproval?.allowed === true &&
    entry.action.messageApproval.tier !== 'high' &&
    Date.parse(entry.action.expiresAt) > Date.now()
    ? entry
    : null;
}
